/**
 * CRO blocks: quantity breaks, bundle offer, sticky add to cart, upsell.
 *
 * ── The one rule these components follow ───────────────────────────────────
 * The theme never invents a price. Every tier and bundle option resolves to a
 * real (variant id, quantity) pair that goes through the normal product form
 * and the normal Shopify cart. What a tier *displays* is a projection; what
 * the customer pays is whatever Shopify calculates. Where the two can differ,
 * the block says so on screen.
 *
 * There is exactly one add-to-cart path in the theme: the product form in the
 * buy-buttons block. Sticky ATC and bundle submit that same form rather than
 * running a second cart flow.
 */
(function () {
  'use strict';

  const { cart, pubsub, utils, strings } = window.Theme;

  /** Reads the shared product state for whichever section an element is in. */
  function stateFor(element) {
    const page = element.closest('product-page');
    return page ? window.Theme.product.get(page.dataset.sectionId) : null;
  }

  function sectionIdFor(element) {
    return element.closest('product-page')?.dataset.sectionId;
  }

  /**
   * Claims the form's quantity field for one component.
   *
   * A product form must carry exactly one `quantity` value. Both the quantity
   * break block and a quantity-mode bundle want to drive it, so the first one
   * to connect takes ownership and any later one keeps its own UI but stops
   * writing — otherwise the two silently overwrite each other and the customer
   * gets whichever block happened to initialise last.
   *
   * @returns {HTMLInputElement|null} the input to write, or null if not owner
   */
  function claimQuantityField(component, fallbackInput) {
    const page = component.closest('product-page');
    const shared = page?.querySelector('input[data-quantity-input][name="quantity"]');
    const target = shared || fallbackInput;
    if (!target) return null;

    const tag = component.tagName.toLowerCase();
    const owner = target.dataset.quantityOwner;

    if (owner && owner !== tag) {
      if (fallbackInput) fallbackInput.disabled = true;

      // A misconfiguration message for the merchant, not debug output: it only
      // speaks in the Theme Editor, where the person who can fix it is
      // standing. Shoppers never see anything in their console.
      if (window.Shopify && window.Shopify.designMode) {
        console.warn(
          `[AKY CRO] <${tag}> is not driving the quantity field because <${owner}> already claims it. ` +
            'Use only one quantity-driving block (quantity breaks OR a quantity-mode bundle) per product.'
        );
      }

      return null;
    }

    target.dataset.quantityOwner = tag;

    // Exactly one enabled `quantity` input reaches FormData.
    if (fallbackInput) fallbackInput.disabled = Boolean(shared);

    return target;
  }

  /* ================================================================== */
  /* Quantity breaks                                                     */
  /* ================================================================== */

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.sectionId = sectionIdFor(this);
      this.mode = this.dataset.mode; // 'unit' | 'discount'
      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
      if (!this.tiers.length) return;

      this.fallbackInput = this.querySelector('[data-qb-quantity]');
      this.resolveQuantityTarget();

      this.addEventListener('change', (event) => {
        const input = event.target.closest('input[type="radio"]');
        if (input) this.select(input.closest('[data-tier]'));
      });

      this.unsubscribe = pubsub.subscribe(pubsub.EVENTS.variantChange, ({ sectionId }) => {
        if (sectionId === this.sectionId) this.render();
      });

      // The quantity can also be changed from the stepper. Without this the
      // tier stayed highlighted while the quantity no longer matched it: the
      // customer saw "2 items - $1,399.90" selected and was charged for one.
      this.unsubscribeQuantity = pubsub.subscribe(pubsub.EVENTS.quantityChange, ({ sectionId, quantity }) => {
        if (sectionId === this.sectionId) this.syncToQuantity(quantity);
      });

      this.render();

      // The merchant may not have marked any tier as default.
      let checked = this.querySelector('input[type="radio"]:checked');
      if (!checked) {
        checked = this.tiers[0].querySelector('input[type="radio"]');
        if (checked) checked.checked = true;
      }
      if (checked) this.select(checked.closest('[data-tier]'));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribeQuantity?.();
    }

    /**
     * Mirrors a quantity set elsewhere onto the tiers.
     *
     * Only the visual selection is touched — this must never write the
     * quantity back, or the stepper and the tiers would bounce off each
     * other. When no tier matches, none stays selected: showing a bundle the
     * customer is not buying is exactly the mismatch this theme forbids.
     */
    syncToQuantity(quantity) {
      const match = this.tiers.find((tier) => (parseInt(tier.dataset.quantity, 10) || 1) === quantity) || null;

      this.tiers.forEach((tier) => {
        tier.toggleAttribute('data-selected', tier === match);
        const radio = tier.querySelector('input[type="radio"]');
        if (radio) radio.checked = tier === match;
      });
    }

    resolveQuantityTarget() {
      this.quantityTarget = claimQuantityField(this, this.fallbackInput);
    }

    select(tier) {
      if (!tier) return;

      this.tiers.forEach((t) => t.toggleAttribute('data-selected', t === tier));

      // Another block owns the quantity field: keep the visual selection but
      // do not fight it for the value.
      if (!this.quantityTarget) return;

      const quantity = parseInt(tier.dataset.quantity, 10) || 1;
      this.quantityTarget.value = quantity;
      // Let the stepper's own listeners (and the canonical state) react.
      this.quantityTarget.dispatchEvent(new Event('change', { bubbles: true }));

      window.Theme.product.setQuantity(this.sectionId, quantity);
    }

    /** Recomputes every tier's figures from the currently selected variant. */
    render() {
      const variant = stateFor(this)?.variant;
      if (!variant) return;

      const unitPrice = variant.price;

      this.tiers.forEach((tier) => {
        const quantity = parseInt(tier.dataset.quantity, 10) || 1;
        const percent = parseFloat(tier.dataset.discountPercent) || 0;

        const gross = unitPrice * quantity;
        // `discount` mode mirrors a discount the merchant configured in
        // Shopify; `unit` mode never claims a reduction.
        const net = this.mode === 'discount' ? Math.round(gross * (1 - percent / 100)) : gross;

        const totalEl = tier.querySelector('[data-tier-total]');
        if (totalEl) totalEl.textContent = utils.formatMoney(net);

        const compareEl = tier.querySelector('[data-tier-compare]');
        if (compareEl) {
          const hasSaving = net < gross;
          compareEl.hidden = !hasSaving;
          if (hasSaving) compareEl.textContent = utils.formatMoney(gross);
        }

        const perItemEl = tier.querySelector('[data-tier-per-item]');
        if (perItemEl) {
          perItemEl.textContent = strings.perItem.replace('[price]', utils.formatMoney(Math.round(net / quantity)));
        }

        const savingEl = tier.querySelector('[data-tier-saving]');
        if (savingEl) {
          const saved = gross - net;
          savingEl.hidden = saved <= 0;
          if (saved > 0) savingEl.textContent = strings.savePercent.replace('[percent]', Math.round(percent));
        }
      });
    }
  }

  if (!customElements.get('quantity-breaks')) customElements.define('quantity-breaks', QuantityBreaks);

  /* ================================================================== */
  /* Bundle offer                                                        */
  /* ================================================================== */

  class BundleOffer extends HTMLElement {
    connectedCallback() {
      this.sectionId = sectionIdFor(this);
      this.mode = this.dataset.mode; // 'option' | 'quantity'
      this.optionIndex = parseInt(this.dataset.optionIndex, 10) || 0;
      this.options = Array.from(this.querySelectorAll('[data-bundle-option]'));
      if (!this.options.length) return;

      this.fallbackInput = this.querySelector('[data-bundle-quantity]');
      this.resolveQuantityTarget();

      this.addEventListener('change', (event) => {
        const input = event.target.closest('input[type="radio"]');
        if (input) this.select(input.closest('[data-bundle-option]'));
      });

      this.unsubscribe = pubsub.subscribe(pubsub.EVENTS.variantChange, ({ sectionId }) => {
        if (sectionId === this.sectionId) this.render();
      });

      this.render();

      const checked = this.querySelector('input[type="radio"]:checked');
      if (checked) this.select(checked.closest('[data-bundle-option]'));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    resolveQuantityTarget() {
      if (this.mode !== 'quantity') return;
      this.quantityTarget = claimQuantityField(this, this.fallbackInput);
    }

    select(option) {
      if (!option) return;
      this.options.forEach((o) => o.toggleAttribute('data-selected', o === option));

      if (this.mode === 'option') {
        // Resolve the option value against the CURRENT selection, so picking a
        // pack size keeps the colour or size the customer already chose.
        const variant = this.resolveVariant(option.dataset.optionValue);
        if (!variant) return;

        const page = this.closest('product-page');
        if (page?.querySelector('variant-picker')) {
          // The picker stays the only writer of the variant id.
          document.dispatchEvent(
            new CustomEvent('variant:request', {
              detail: { sectionId: this.sectionId, variantId: variant.id },
            })
          );
        } else {
          page?.querySelectorAll('[data-variant-id]').forEach((input) => {
            input.value = variant.id;
          });
          window.Theme.product.setVariant(this.sectionId, variant);
        }
        return;
      }

      if (!this.quantityTarget) return;

      const quantity = parseInt(option.dataset.quantity, 10) || 1;
      this.quantityTarget.value = quantity;
      this.quantityTarget.dispatchEvent(new Event('change', { bubbles: true }));
      window.Theme.product.setQuantity(this.sectionId, quantity);
    }

    /** The variant that results from swapping one option value. */
    resolveVariant(value) {
      const state = stateFor(this);
      if (!state?.variant) return null;

      const wanted = [...state.variant.options];
      wanted[this.optionIndex] = value;
      return state.variants.find((v) => v.options.every((opt, i) => opt === wanted[i]));
    }

    render() {
      const state = stateFor(this);
      if (!state?.variant) return;

      this.options.forEach((option) => {
        const totalEl = option.querySelector('[data-bundle-total]');
        const compareEl = option.querySelector('[data-bundle-compare]');
        if (!totalEl) return;

        if (this.mode === 'option') {
          // Real variant prices — nothing is projected here.
          const variant = this.resolveVariant(option.dataset.optionValue);
          if (!variant) {
            option.hidden = true;
            return;
          }

          option.hidden = false;
          option.dataset.variantId = variant.id;
          option.toggleAttribute('data-unavailable', !variant.available);
          option.toggleAttribute('data-selected', variant.id === state.variant.id);
          const radio = option.querySelector('input[type="radio"]');
          if (radio) radio.checked = variant.id === state.variant.id;

          totalEl.textContent = utils.formatMoney(variant.price);
          if (compareEl) {
            const hasCompare = variant.compare_at_price > variant.price;
            compareEl.hidden = !hasCompare;
            if (hasCompare) compareEl.textContent = utils.formatMoney(variant.compare_at_price);
          }
        } else {
          const quantity = parseInt(option.dataset.quantity, 10) || 1;
          const percent = parseFloat(option.dataset.discountPercent) || 0;
          const gross = state.variant.price * quantity;
          const net = Math.round(gross * (1 - percent / 100));

          totalEl.textContent = utils.formatMoney(net);
          if (compareEl) {
            compareEl.hidden = net >= gross;
            if (net < gross) compareEl.textContent = utils.formatMoney(gross);
          }
        }
      });
    }
  }

  if (!customElements.get('bundle-offer')) customElements.define('bundle-offer', BundleOffer);

  /* ================================================================== */
  /* Sticky add to cart                                                  */
  /* ================================================================== */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = sectionIdFor(this);
      this.page = this.closest('product-page');

      this.titleEl = this.querySelector('[data-sticky-variant-title]');
      this.imageEl = this.querySelector('[data-sticky-image]');

      this.unsubscribe = pubsub.subscribe(pubsub.EVENTS.variantChange, ({ sectionId, variant }) => {
        if (sectionId === this.sectionId) this.sync(variant);
      });

      this.observeTrigger();
      this.sync(stateFor(this)?.variant);
      this.publishHeight();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.observer?.disconnect();
      document.documentElement.style.removeProperty('--sticky-atc-height');
    }

    /**
     * The bar appears once the real add-to-cart button leaves the viewport.
     * The form is the documented anchor, so no other block has to cooperate.
     */
    observeTrigger() {
      const trigger = this.page?.querySelector('form[data-product-form]');
      if (!trigger || !('IntersectionObserver' in window)) {
        this.dataset.visible = 'true';
        return;
      }

      this.observer = new IntersectionObserver(([entry]) => this.applyEntry(entry), { threshold: 0 });
      this.observer.observe(trigger);
    }

    /**
     * Show the bar only once the form has scrolled ABOVE the viewport. When
     * the form is still below the fold the customer has not reached the buy
     * button yet, and a sticky bar would just cover content.
     */
    static shouldShow(entry) {
      return !entry.isIntersecting && entry.boundingClientRect.top < 0;
    }

    applyEntry(entry) {
      this.dataset.visible = String(StickyAtc.shouldShow(entry));
      this.publishHeight();
    }

    publishHeight() {
      const height = this.dataset.visible === 'true' ? this.offsetHeight : 0;
      document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`);
    }

    sync(variant) {
      if (!variant) return;

      if (this.titleEl) {
        this.titleEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
      }

      if (this.imageEl && variant.featured_image?.src) {
        // Shopify image URLs accept a width parameter for a smaller render.
        this.imageEl.src = `${variant.featured_image.src}&width=160`.replace('?&', '?');
        this.imageEl.srcset = '';
      }
      // Price is handled by the shared [data-variant-price] hook.
    }
  }

  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ================================================================== */
  /* Upsell                                                              */
  /* ================================================================== */

  class ProductUpsell extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-upsell-add]');
        if (button) this.add(button);
      });

      if (this.dataset.source === 'recommendations') this.loadRecommendations();
    }

    /**
     * Server-rendered recommendations. Shopify's Recommendations API returns
     * rendered section HTML, so the cards are built by Liquid and match the
     * rest of the theme exactly.
     */
    async loadRecommendations() {
      const { productId, limit, intent } = this.dataset;
      if (!productId) return;

      const url = new URL(`${window.Theme.config.routes.root}recommendations/products`, window.location.origin);
      url.searchParams.set('section_id', 'product-recommendations');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', limit || 4);
      url.searchParams.set('intent', intent || 'related');

      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const html = await response.text();
        const fresh = utils.parseSection(html, '[data-upsell-list]');
        const target = this.querySelector('[data-upsell-list]');
        if (fresh && target && fresh.children.length) {
          target.replaceWith(fresh);
          this.hidden = false;
        }
      } catch {
        // A failed recommendation fetch must never break the product page.
      }
    }

    async add(button) {
      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      button.classList.add('button--loading');
      button.setAttribute('aria-disabled', 'true');

      try {
        await cart.add({ id: variantId, quantity: 1 });
        button.textContent = strings.added;
      } catch {
        button.removeAttribute('aria-disabled');
      } finally {
        button.classList.remove('button--loading');
      }
    }
  }

  if (!customElements.get('product-upsell')) customElements.define('product-upsell', ProductUpsell);

  /* ================================================================== */
  /* Quantity gifts (product page, informational)                        */
  /* ================================================================== */

  class QuantityGifts extends HTMLElement {
    connectedCallback() {
      this.sectionId = sectionIdFor(this);
      this.items = Array.from(this.querySelectorAll('[data-gift]'));
      if (!this.items.length) return;

      this.unsubscribe = pubsub.subscribe(pubsub.EVENTS.quantityChange, ({ sectionId, quantity }) => {
        if (sectionId === this.sectionId) this.render(quantity);
      });

      this.render(stateFor(this)?.quantity ?? 1);
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    render(quantity) {
      this.items.forEach((item) => {
        const needed = parseInt(item.dataset.minQuantity, 10) || 1;
        item.toggleAttribute('data-unlocked', quantity >= needed);
      });
    }
  }

  if (!customElements.get('quantity-gifts')) customElements.define('quantity-gifts', QuantityGifts);
})();
